import React, { useState } from 'react';
import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import { formatPhone, personName } from '../../voice/callFormat';
import { formatDateTime, missedLine, uploadedDay, whoLines } from './missedCallFormat';

/**
 * 복구 — put a soft-deleted row back (plan §6.2). Any admin, the same as deleting one.
 *
 * It says what is coming back before it happens, because a restored row reappears on every admin's list: which row,
 * whose number, and who deleted it. The 번호 is kept — deleted rows still count when the next one is handed out, so
 * a number is never reused — and the row's call, AI 요약 and task badge return with it, since a soft delete never
 * touched the call.
 *
 * The one thing worth warning about: repeat rows are allowed, so if this number was added again on that day after
 * the delete, restoring leaves two rows for it.
 */
const RestoreDialog = ({ row, open, onClose, onRestore }) => {
    const [busy, setBusy] = useState(false);

    if (!row) return null;

    const restore = async () => {
        if (busy) return;
        setBusy(true);
        await onRestore(row);
        setBusy(false);
        onClose();
    };

    const who = whoLines(row).map((l) => l.text).join(', ');

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" disableEnforceFocus>
            <DialogTitle>이 행을 복구할까요?</DialogTitle>
            <DialogContent>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {uploadedDay(row)} #{row.daily_seq} · {formatPhone(row.phone_number)}
                </Typography>
                {who && <Typography variant="body2" color="text.secondary">{who}</Typography>}
                {missedLine(row) && (
                    <Typography variant="body2" color="text.secondary">{missedLine(row)}</Typography>
                )}

                <Typography variant="body2" sx={{ mt: 1.5 }}>
                    삭제: <b>{personName(row.deleted_by) || '알 수 없음'}</b>
                    {row.deleted_at ? ` · ${formatDateTime(row.deleted_at)}` : ''}
                </Typography>

                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
                    번호(#{row.daily_seq})는 그대로 돌아오고, 이 행에서 걸었던 통화와 AI 요약, 학생카드반영도 함께 돌아옵니다.
                    복구 내역은 기록에 남습니다.
                </Typography>
                <Alert severity="info" sx={{ mt: 1.5 }}>
                    삭제한 뒤 이 번호를 같은 날짜에 다시 추가했다면, 복구하면 같은 번호의 행이 두 개가 됩니다.
                </Alert>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>취소</Button>
                <Button variant="contained" onClick={restore} disabled={busy}>복구</Button>
            </DialogActions>
        </Dialog>
    );
};

export default RestoreDialog;
